import React, { useContext, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import firebase from 'firebase/compat/app';
import { firebaseAuth } from '../../../providers/AuthProvider';
import { userRoles } from '../../../Constants';
import { toast } from 'react-toastify';

const ROLE_ERROR_LABELS = {
  INVALID_EMAIL: 'Please enter a valid email address.',
  INVALID_ROLE: 'Please select a role.',
  USER_ALREADY_VERIFIED:
    'A user with this email already exists and has verified their account.',
  INVITE_FAILED:
    'Could not create the invite. The user has been rolled back; please try again.',
};

const Invite = () => {
  const { user } = useContext(firebaseAuth);
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', role: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!user?.isAuthenticated) return <Navigate to="/manage/sign-in" />;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const email = form.email.trim().toLowerCase();
    const role = Number(form.role);
    if (!email) return setError('INVALID_EMAIL');
    if (!Number.isInteger(role) || role < 1) return setError('INVALID_ROLE');

    setSubmitting(true);
    try {
      const inviteUser = firebase.functions().httpsCallable('appV2/inviteUser');
      const result = await inviteUser({ email, role });
      if (result?.data?.error) {
        setError(result.data.error);
        return;
      }
      toast.success('Invitation sent. The link expires in 7 days.');
      navigate('/manage/users');
    } catch (err) {
      const code = err?.details?.error || err?.message || 'INVITE_FAILED';
      setError(code);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    'mt-2 block w-full rounded-md border-0 bg-zinc-50 px-3 py-2 text-sm text-zinc-900 shadow-sm ring-1 ring-inset ring-zinc-300 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-zinc-900 dark:bg-zinc-800 dark:text-zinc-100 dark:ring-white/10 dark:placeholder:text-zinc-500 dark:focus:ring-white';

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-100">Invite user</h1>
      <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
        Send an email invitation. The recipient will verify their email and choose a
        password before being prompted to enroll multi-factor authentication.
      </p>

      <form onSubmit={handleSubmit} className="mt-8 space-y-6">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-zinc-900 dark:text-zinc-100">
            Email
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            value={form.email}
            onChange={handleChange}
            className={inputCls}
          />
        </div>
        <div>
          <label htmlFor="role" className="block text-sm font-medium text-zinc-900 dark:text-zinc-100">
            Role
          </label>
          <select
            id="role"
            name="role"
            value={form.role}
            onChange={handleChange}
            className={inputCls}
          >
            <option value="" />
            {userRoles.map((label, index) => (
              <option key={label} value={index + 1}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-inset ring-red-200 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/20">
            {ROLE_ERROR_LABELS[error] || error}
          </div>
        )}

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center justify-center rounded-md bg-zinc-900 px-3 py-2 text-sm font-semibold text-zinc-50 shadow-sm hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100"
          >
            {submitting ? 'Sending…' : 'Send invite'}
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={() => navigate('/manage/users')}
            className="inline-flex items-center justify-center rounded-md bg-zinc-50 px-3 py-2 text-sm font-semibold text-zinc-900 ring-1 ring-inset ring-zinc-300 hover:bg-zinc-100 disabled:opacity-60 dark:bg-zinc-800 dark:text-zinc-100 dark:ring-white/10 dark:hover:bg-zinc-700"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default Invite;
